import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import NagDashboardTableRow from './NagDashboardTableRow';
import {
  accountStatusTypesArray,
  BASIC_ACCOUNT_STEP,
  nagDashboardStepsArray,
  REQUIRES_ATTENTION_BANNER_TYPES,
  UPGRADE_STATUS_TYPES,
  VERIFIED_NON_POST_PAID,
  VERIFIED_POST_PAID,
  VERIFY_ACCOUNT_STEP,
} from '../../constants/nagDashboardContants';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  getCurrentAccountCode,
  getCurrentAccountUid,
  selectNagDashboard,
} from '../../store/selectors/accountSelectors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { JWT_TOKEN } from '../../constants/localStorage';
import { authScopeStringGetHelper } from '../../util/helpers/authScopeHelpers';
import { BUY_PAGE, FINANCIAL_BALANCES_PAGE } from '../../constants/pages';

const NagDashboardVerifyAccountStep = ({ isShortFlow, activeStep }) => {
  const { t } = useTranslation();
  const { RegistrationUrlV2 } = useSelector(selectSettings);
  const token = authScopeStringGetHelper(JWT_TOKEN);
  const accountUid = useSelector(getCurrentAccountUid);
  const accountCode = useSelector(getCurrentAccountCode);
  const nagDashboard = useSelector(selectNagDashboard);
  const history = useHistory();
  const registrationApi = RegistrationUrlV2;
  const goToRegistrationPage = () => {
    window.location.replace(
      `${registrationApi}/choose-application?accountCode=${accountCode}&accountUid=${accountUid}&token=${token}`,
    );
  };

  const nonVerifiedAccountVerifiedStep = {
    title: t('nagDashboard.verifyAccountStep.title'),
    description: t(
      'nagDashboard.verifyAccountStep.nonVerifiedAccount.verifiedDescription',
    ),
    status: 'ActionRequired',
  };

  const nonVerifiedAccountNoDocumentsStep = {
    title: t('nagDashboard.verifyAccountStep.title'),
    description: t(
      'nagDashboard.verifyAccountStep.nonVerifiedAccount.noDocumentsDescription',
    ),
    status: 'ActionRequired',
  };

  const nonVerifiedAccountPendingStep = {
    title: t('nagDashboard.verifyAccountStep.title'),
    description: t(
      'nagDashboard.verifyAccountStep.nonVerifiedAccount.pendingDescription',
    ),
    status: 'Pending',
  };

  const isVerifiedAccount =
    parseEnumType(nagDashboardStepsArray, activeStep) === VERIFIED_POST_PAID ||
    parseEnumType(nagDashboardStepsArray, activeStep) ===
      VERIFIED_NON_POST_PAID;

  const isPendingAccount =
    parseEnumType(accountStatusTypesArray, nagDashboard?.AccountStatusType) ===
    'Pending';

  const isUpgradeProgress =
    nagDashboard.UpgradeStatusType === UPGRADE_STATUS_TYPES.InProgress;

  const isUpgraded =
    nagDashboard.UpgradeStatusType === UPGRADE_STATUS_TYPES.Upgraded;

  const isPending = isPendingAccount || (isPendingAccount && !isUpgraded);

  const requiresAttentionBannerType = nagDashboard?.RequiresAttentionBannerType;
  const requiresAttentionStep =
    requiresAttentionBannerType ===
    REQUIRES_ATTENTION_BANNER_TYPES.RequiresAttentionNoDocumentsRequested
      ? nonVerifiedAccountNoDocumentsStep
      : nonVerifiedAccountVerifiedStep;

  const getBasicAccountBannerText = () => {
    switch (history.location.pathname) {
      case FINANCIAL_BALANCES_PAGE:
        return {
          title: t(
            'nagDashboard.verifyAccountStep.basicAccount.financialBalances.title',
          ),
          description: t(
            'nagDashboard.verifyAccountStep.basicAccount.financialBalances.description',
          ),
          status: 'GetStarted',
        };
      case BUY_PAGE:
        return {
          title: t('nagDashboard.verifyAccountStep.basicAccount.buy.title'),
          description: t(
            'nagDashboard.verifyAccountStep.basicAccount.buy.description',
          ),
          status: 'GetStarted',
        };
      default:
        return {
          title: t('nagDashboard.verifyAccountStep.basicAccount.default.title'),
          description: t(
            'nagDashboard.verifyAccountStep.basicAccount.default.description',
          ),
          status: 'GetStarted',
        };
    }
  };

  const basicAccountVerifiedStepInfo = getBasicAccountBannerText();

  const getDashboardVerifiedAccount = () => {
    switch (parseEnumType(nagDashboardStepsArray, activeStep)) {
      case BASIC_ACCOUNT_STEP:
        return {
          ...basicAccountVerifiedStepInfo,
          buttonText: isUpgradeProgress
            ? t('nagDashboard.verifyAccountStep.buttons.continue')
            : t('nagDashboard.verifyAccountStep.buttons.getStarted'),
          action: () => {
            goToRegistrationPage();
          },
        };
      case VERIFY_ACCOUNT_STEP:
        return isPending
          ? nonVerifiedAccountPendingStep
          : requiresAttentionStep;

      default:
    }
  };

  const dashboardData = getDashboardVerifiedAccount();

  if (!isVerifiedAccount) {
    return (
      <NagDashboardTableRow
        title={dashboardData.title}
        description={dashboardData.description}
        action={dashboardData.action}
        buttonText={dashboardData.buttonText}
        status={dashboardData.status}
        isActive={
          parseEnumType(nagDashboardStepsArray, activeStep) ===
            VERIFY_ACCOUNT_STEP && !isPending
        }
        isShortFlow={isShortFlow}
        isPendingVerificationStatus={
          parseEnumType(nagDashboardStepsArray, activeStep) ===
            VERIFY_ACCOUNT_STEP && !isPending
        }
        activeStep={activeStep}
      />
    );
  }
  return <></>;
};

NagDashboardVerifyAccountStep.propTypes = {
  isShortFlow: PropTypes.string,
  activeStep: PropTypes.number,
};

export default NagDashboardVerifyAccountStep;
